import {
	type CollectionModel,
	CollectionModelFieldImage,
	CollectionModelFieldSubForm,
	CollectionModelFieldTrueSize,
} from '@ikegps/core-shared';
import { T, Translate } from '../../../i18n';
import { AppStore } from '../../../store';
import { DataGrid } from '../components/DataGrid';
import type { ImageReference } from '../components/ImageComponent';
import { ImageGrid } from '../components/ImageGrid';
import { ImageWithTitle } from '../components/ImageWithTitle';
import type { SubForm } from '../components/SubForm';
import { TitleBar } from '../components/TitleBar';
import { DataExtractor } from '../DataExtractor';
import {
	captureReferenceString,
	compositeURL,
	extractSubforms,
	hasUserMeasurement,
	isCaptureHidden,
	isDataGridField,
} from '../ReportHelpers';
import { ReportBase } from './ReportBase';

export class Report extends ReportBase {
	constructor(poles: CollectionModel[]) {
		super(poles);
	}

	buildPDF(): void {
		this.poles.forEach((pole, i) => {
			const job = AppStore.Tag.get(pole.jobId);
			const fullImages: ImageWithTitle[] = [];
			const imageGroups: Record<string, ImageReference[]> = {};
			const tagArr: [string, string][] = [];
			const subformArray: SubForm[] = [];

			// Title
			this.nodes.push(
				new TitleBar(pole.title || Translate(T.COLLECTION_MISSING_TITLE), job.name, i + 1, this.poles.length)
			);

			// Data grid of items outside of subforms
			pole.forEachField((field) => {
				if (field.parent.parent == null) {
					if (isDataGridField(field)) {
						const extractedValue = DataExtractor.createPrintableValue(field);
						tagArr.push([extractedValue.title, extractedValue.value]);
					}

					if (field instanceof CollectionModelFieldSubForm) {
						subformArray.push(...extractSubforms(pole, field, this.images));
					}

					if (field instanceof CollectionModelFieldTrueSize) {
						const captures = field.getOwnedCaptures();
						captures.forEach((capture) => {
							if (!isCaptureHidden(pole, capture.objectId)) {
								const imageReferenceString = captureReferenceString(pole, capture);
								this.images[imageReferenceString] = compositeURL(pole, capture);
								const imgReference: ImageReference = { reference: imageReferenceString };

								// If a capture has measurements, then it has the timestamp built into the photo
								if (hasUserMeasurement(pole, capture.objectId)) {
									fullImages.push(new ImageWithTitle(field.schema.displayName, imgReference));
								} else {
									imgReference.timeStamp = new Date(capture.createdAt);
								}

								if (!imageGroups[field.schema.displayName]) {
									imageGroups[field.schema.displayName] = [];
								}
								imageGroups[field.schema.displayName]?.push(imgReference);
							}
						});
					}

					if (field instanceof CollectionModelFieldImage) {
						const captures = field.getOwnedCaptures();

						captures.forEach((capture) => {
							if (!isCaptureHidden(pole, capture.objectId)) {
								const imageReferenceString = captureReferenceString(pole, capture);
								// For Image fields, use composite if crop is set, otherwise use medium
								const hasCrop = pole.getCropParameterState(capture.objectId).useInExportCorrections();
								this.images[imageReferenceString] = hasCrop ? compositeURL(pole, capture) : capture.medium.url;
								if (!imageGroups[field.schema.displayName]) {
									imageGroups[field.schema.displayName] = [];
								}
								imageGroups[field.schema.displayName]?.push({
									reference: imageReferenceString,
									timeStamp: new Date(capture.createdAt),
								});
							}
						});
					}
				}
			});

			this.nodes.push(new DataGrid(tagArr));

			this.nodes.push(new ImageGrid(imageGroups));

			// Subforms
			subformArray.forEach((subform) => {
				this.nodes.push(subform);
			});

			// Full size composite images
			this.nodes.push(...fullImages);
		});
	}
}
